import { useLayoutEffect, useRef, useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import { HeroSearchForm, TrustPilotWidget, type FC, type FCWC, type ResponsiveImageType } from ".."
import HeroMedia from "./HeroMedia"

type HeroProps = {
  title: string
  subtitle: string
  media: ResponsiveImageType[]
  video?: string
}

const HeroHome: FC<HeroProps> = ({ title, subtitle, media, video }) => {
  const carouselRef = useRef<HTMLDivElement>(null)
  const [viewportHeight, setViewportHeight] = useState<number>(0)
  useLayoutEffect(() => {
    setViewportHeight(window.innerHeight)
  }, [])

  return (
    <HeroContainer>
      <HeroInner>
        <HeroMedia
          video={video}
          media={media}
          containerRef={carouselRef}
          arrowsVisible={false}
          shiftPosition
        />
        <HeroContent>
          <Box>
            <Typography
              variant="heading.3xl"
              component="h1"
              color={(theme) => theme.palette["neutral.100"].main}
              textAlign="center"
              lineHeight={0.9}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body.lg"
                component="h3"
                color={(theme) => theme.palette["neutral.100"].main}
                textAlign="center"
                mt={{ xs: 5, md: 6 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            position={{ xs: "absolute", md: "relative" }}
            bottom={{ xs: 54, md: 0 }}
            left={{ xs: "50%", md: "auto" }}
            px={3}
            width="100%"
            maxWidth={1100}
            sx={{
              transform: { xs: "translateX(-50%)", md: "none" },
            }}
            zIndex={100}
          >
            <HeroSearchForm variant="home" />
          </Box>
          <TrustPilot />
        </HeroContent>
        {media?.length > 0 && (
          <Box position="absolute" top="0" left="0" bottom="0" right="0" ref={carouselRef} />
        )}
      </HeroInner>
    </HeroContainer>
  )
}

export default HeroHome

/** A sub-component for the section container of the hero content */
const HeroContainer: FCWC<{}> = ({ children, ...props }) => (
  <Stack
    component="header"
    alignItems="stretch"
    justifyContent="flex-start"
    width="100%"
    position="relative"
    pt={{ xs: 16, md: 32.5 }}
    sx={{ isolation: "isolate" }}
    zIndex={1}
    {...props}
  >
    {children}
  </Stack>
)

/** A sub-component for the container of the hero content */
const HeroContent: FCWC<{}> = ({ children }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    height="100%"
    p={3}
    position="relative"
    zIndex={3}
    gap={{ xs: 6, md: 9 }}
  >
    {children}
  </Stack>
)

/** A sub-component for the trust pilot widget wrapper */
const TrustPilot: FC = () => {
  return (
    <Box
      zIndex={3}
      mt={{ xs: 0, lg: 10 }}
      left={(theme) => theme.spacing(3)}
      bottom={(theme) => theme.spacing(3)}
      sx={(theme) => ({
        fontSize: "0.75rem!important",
        background: "rgba(0, 0, 0, 0.50)",
        borderRadius: 1,
        backdropFilter: "blur(4px)",
        padding: theme.spacing(3, 0),
      })}
    >
      <TrustPilotWidget variant="hero" />
    </Box>
  )
}

/** A sub-component for the section container of the hero content */
const HeroInner: FCWC = ({ children }) => (
  <Stack
    height={{ xs: 570, md: 670 }}
    padding={{ xs: 2, md: 2.5 }}
    sx={{
      isolation: "isolate",
      position: "relative",
    }}
  >
    <Stack
      justifyContent="flex-end"
      height="100%"
      position="relative"
      sx={{ borderRadius: { md: 1.5 } }}
    >
      {children}
    </Stack>
  </Stack>
)
